import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ModelPicker from '../Common/ModelPicker';
import { settingsApi } from '../../api/settings';
import {
  CANT_ESTIMATE_NOW_LINE,
  CANT_ESTIMATE_RATES_LINE,
  CANT_ESTIMATE_SIZE_LINE,
  ESTIMATE_SLOW_MS,
  PROSE_SCENE_GENERATION_DEFAULT_MODEL,
  PROSE_SCENE_GENERATION_KEY,
  estimateSceneWritingCostUsd,
  formatSceneWritingEstimateLine,
  pricingFromCatalog,
  resolveProseModelId,
} from '../../utils/estimateSceneWritingCost';
import { bandShowsRange } from '../../utils/estimateOutlineCost';
import {
  WRITE_DRAFT_GENDER_LEAD,
  WRITE_DRAFT_GENDER_MAN,
  WRITE_DRAFT_GENDER_WOMAN,
  WRITE_DRAFT_INCLUDES_CHECK,
  WRITE_DRAFT_KEEPS_HAND_EDITS,
  WRITE_DRAFT_REPLACES_EDITED,
} from '../../utils/writerCopy';
import './WriteDraftConfirm.css';

// Last rates that arrived. A remount (the writing surface swaps in while
// the confirm is open) can cancel the in-flight read; the next mount
// shows the figure it already has instead of staying on "Loading estimate…".
let ratesCache = null;

/** Test-only. Rates are remembered across mounts in a visit. */
export function resetWriteDraftRatesCacheForTests() {
  ratesCache = null;
}

/**
 * Confirm before Scene Writing Write draft (#1193).
 * Typical-to-worst dollar band from the live prompt size and the
 * writer's Prose Generation model. The picker reads and writes that
 * preference. Cancel writes nothing. A missing rate, or a prompt size
 * that failed or timed out, still allows confirm. This is not a spend cap.
 *
 * Confirm stays off until the rates, the model name, and the prompt
 * size have loaded. The same loading line covers all three. A slow
 * read can still confirm once the model name is on screen. After the
 * read settles without rates, Confirm uses the saved preference or
 * the product default.
 */
export default function WriteDraftConfirm({
  estimatedInputTokens = null,
  promptChars = null,
  outputBudget = null,
  typicalOutput = null,
  draftCheck = null,
  sizePending = false,
  confirming = false,
  notice = '',
  replacesEditedDraft = false,
  handEditedAccepted = false,
  genderNeeded = [],
  styleChoice = null,
  onConfirm,
  onCancel,
}) {
  const [modelId, setModelId] = useState(
    () => ratesCache?.modelId || PROSE_SCENE_GENERATION_DEFAULT_MODEL,
  );
  const [catalog, setCatalog] = useState(() => ratesCache?.catalog ?? null);
  const [ratesState, setRatesState] = useState(() => ratesCache?.ratesState || 'loading');
  const [modelReady, setModelReady] = useState(false);
  const [slow, setSlow] = useState(false);
  const [genderChoices, setGenderChoices] = useState({});
  const markModelReady = useCallback(() => setModelReady(true), []);
  const people = (Array.isArray(genderNeeded) ? genderNeeded : [])
    .filter((person) => person && person.name);
  const genderKey = (person) => person.note_id || person.noteId || person.name;
  const gendersReady = people.every((person) => genderChoices[genderKey(person)]);

  useEffect(() => {
    const timer = setTimeout(() => setSlow(true), ESTIMATE_SLOW_MS);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!ratesCache) setRatesState('loading');
    const pull = async () => {
      const [settingsRes, catalogRes] = await Promise.all([
        settingsApi.getSettings(),
        settingsApi.getOpenRouterCatalog().catch(() => null),
      ]);
      const settings = settingsRes?.data || settingsRes || {};
      const preferred = resolveProseModelId(settings?.model_preferences)
        || PROSE_SCENE_GENERATION_DEFAULT_MODEL;
      const models = catalogRes?.data?.models || catalogRes?.models || [];
      return { preferred, models };
    };
    const applyMissing = (preferred) => {
      setModelId((current) => current || preferred);
      setCatalog([]);
      setRatesState('missing');
      ratesCache = {
        modelId: preferred,
        catalog: [],
        ratesState: 'missing',
      };
    };
    const applyReady = (preferred, models) => {
      setModelId((current) => {
        if (
          current
          && current !== PROSE_SCENE_GENERATION_DEFAULT_MODEL
          && current !== preferred
        ) {
          return current;
        }
        return preferred;
      });
      setCatalog(models);
      setRatesState('ready');
      ratesCache = { modelId: preferred, catalog: models, ratesState: 'ready' };
    };
    (async () => {
      let preferred = PROSE_SCENE_GENERATION_DEFAULT_MODEL;
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const result = await pull();
          if (cancelled) return;
          preferred = result.preferred;
          if (result.models.length) {
            applyReady(result.preferred, result.models);
            return;
          }
        } catch {
          if (cancelled) return;
        }
      }
      if (!cancelled) applyMissing(preferred);
    })();
    return () => { cancelled = true; };
  }, []);

  const resolvedModel = (modelId || '').trim() || PROSE_SCENE_GENERATION_DEFAULT_MODEL;
  const pricing = pricingFromCatalog(catalog, resolvedModel);
  const checkModel = (draftCheck?.model_id || draftCheck?.modelId || '').trim();
  const checkPricing = checkModel ? pricingFromCatalog(catalog, checkModel) : null;
  const checkInput = Number(draftCheck?.estimated_input_tokens ?? draftCheck?.estimatedInputTokens);
  const checkTypical = Number(draftCheck?.typical_output_tokens ?? draftCheck?.typicalOutput);
  const checkWorst = Number(draftCheck?.worst_output_tokens ?? draftCheck?.worstOutput);
  const extraParts = checkPricing && [checkInput, checkTypical, checkWorst].every(Number.isFinite)
    ? [{
      id: 'draft_check',
      pricing: checkPricing,
      inputTokens: checkInput,
      typicalOutput: checkTypical,
      worstOutput: checkWorst,
    }]
    : [];
  const hasSize = estimatedInputTokens != null || promptChars != null;
  const band = ratesState === 'ready' && hasSize
    ? estimateSceneWritingCostUsd(pricing, {
      estimatedInputTokens,
      promptChars,
      outputBudget,
      typicalOutput,
      extraParts,
    })
    : null;
  const waitingOnRates = ratesState === 'loading';
  const waitingOnSize = ratesState === 'ready' && !hasSize && sizePending;
  let estimateLine = 'Loading estimate…';
  if ((waitingOnRates || waitingOnSize) && slow) {
    estimateLine = CANT_ESTIMATE_NOW_LINE;
  } else if (ratesState === 'missing') {
    estimateLine = CANT_ESTIMATE_RATES_LINE;
  } else if (ratesState === 'ready' && !hasSize) {
    estimateLine = sizePending ? 'Loading estimate…' : CANT_ESTIMATE_SIZE_LINE;
  } else if (ratesState === 'ready') {
    estimateLine = formatSceneWritingEstimateLine(band) || CANT_ESTIMATE_RATES_LINE;
  }

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onCancel?.();
  };

  return createPortal(
    <div className="sw-write-confirm-backdrop" onClick={handleBackdrop}>
      <div
        className="sw-write-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sw-write-confirm-title"
      >
        <h3 id="sw-write-confirm-title" className="sw-write-confirm__title">
          Write draft
        </h3>
        {replacesEditedDraft ? (
          <p className="sw-write-confirm__hint" data-testid="sw-write-replaces-edited">
            {WRITE_DRAFT_REPLACES_EDITED}
          </p>
        ) : null}
        {handEditedAccepted ? (
          <p className="sw-write-confirm__hint" data-testid="sw-write-keeps-hand-edits">
            {WRITE_DRAFT_KEEPS_HAND_EDITS}
          </p>
        ) : null}
        {people.length > 0 ? (
          <fieldset className="sw-write-confirm__genders" data-testid="sw-write-genders">
            <legend className="sw-write-confirm__label">Gender</legend>
            <p className="sw-write-confirm__hint">{WRITE_DRAFT_GENDER_LEAD}</p>
            {people.map((person) => {
              const key = genderKey(person);
              const picked = genderChoices[key];
              return (
                <div key={key} className="sw-write-confirm__gender" role="group" aria-label={`Gender for ${person.name}`}>
                  <span className="sw-write-confirm__gender-name">{person.name}</span>
                  <button
                    type="button"
                    className="sw-write-confirm__btn sw-write-confirm__choice"
                    aria-pressed={picked === 'woman'}
                    onClick={() => setGenderChoices((current) => ({ ...current, [key]: 'woman' }))}
                  >
                    {WRITE_DRAFT_GENDER_WOMAN}
                  </button>
                  <button
                    type="button"
                    className="sw-write-confirm__btn sw-write-confirm__choice"
                    aria-pressed={picked === 'man'}
                    onClick={() => setGenderChoices((current) => ({ ...current, [key]: 'man' }))}
                  >
                    {WRITE_DRAFT_GENDER_MAN}
                  </button>
                </div>
              );
            })}
          </fieldset>
        ) : null}
        <p className="sw-write-confirm__hint">
          {bandShowsRange(band)
            ? 'A typical draft costs the lower figure. A full-length draft costs the higher. '
            : ''}
          {WRITE_DRAFT_INCLUDES_CHECK}{' '}
          You still accept or revise the scene.
        </p>
        <div className="sw-write-confirm__picker">
          <span className="sw-write-confirm__label">Model</span>
          <ModelPicker
            mode="select"
            functionKey={PROSE_SCENE_GENERATION_KEY}
            onChange={(id) => setModelId(id || '')}
            onReady={markModelReady}
          />
        </div>
        {styleChoice}
        <p className="sw-write-confirm__estimate" data-testid="sw-write-estimate">
          {estimateLine}
        </p>
        {notice ? (
          <p className="sw-write-confirm__notice" role="status">{notice}</p>
        ) : null}
        <div className="sw-write-confirm__actions">
          <button
            type="button"
            className="sw-write-confirm__btn sw-write-confirm__btn--cancel"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sw-write-confirm__btn sw-write-confirm__btn--confirm"
            disabled={
              confirming
              || ((waitingOnRates || waitingOnSize) && !slow)
              || !modelReady
              || (people.length > 0 && !gendersReady)
            }
            onClick={() => {
              if (!people.length) {
                onConfirm?.(resolvedModel);
                return;
              }
              onConfirm?.(resolvedModel, people.map((person) => ({
                note_id: person.note_id || person.noteId || null,
                name: person.name,
                gender: genderChoices[genderKey(person)],
              })));
            }}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
